import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../config";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export default function JournalPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [linkedProduct, setLinkedProduct] = useState(null);

  useEffect(() => {
    setPost(null);
    setLinkedProduct(null);
    axios
      .get(`${API}/journal/${slug}`)
      .then((res) => {
        setPost(res.data);
        if (res.data.product_slug) {
          axios.get(`${API}/products/${res.data.product_slug}`).then((r) => setLinkedProduct(r.data)).catch(() => {});
        }
      })
      .catch(() => {});
    axios.get(`${API}/journal`).then((res) => setOthers(res.data.filter((p) => p.slug !== slug).slice(0, 2))).catch(() => {});
  }, [slug]);

  return (
    <main data-testid="journal-post-page">
      <Header />
      {post && (
        <>
          <Seo title={post.seo_title || `${post.title} | PhudG`} description={post.seo_description || post.excerpt} image={post.image} />
          <article className="bg-cream pt-36 pb-24">
            <div className="max-w-3xl mx-auto px-6">
              <Link
                to="/journal"
                data-testid="journal-back-link"
                className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-ink/40 hover:text-amberbrand transition-colors duration-300 mb-10"
              >
                <ArrowLeft size={14} /> The Journal
              </Link>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-[10px] tracking-[0.3em] uppercase text-amberbrand">{post.category}</span>
                <span className="text-[10px] tracking-[0.2em] uppercase text-ink/35">{post.published} · {post.read_time}</span>
              </div>
              <motion.h1
                data-testid="journal-post-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif text-4xl md:text-6xl text-ink tracking-tighter leading-tight mb-10"
              >
                {post.title}
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="overflow-hidden rounded-3xl border border-ink/10 mb-12"
              >
                <img src={post.image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
              </motion.div>
              {post.body.map((para, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  className={
                    i === 0
                      ? "font-serif text-xl md:text-2xl text-ink/80 leading-relaxed mb-8"
                      : "text-base font-light text-ink/65 leading-relaxed mb-6"
                  }
                >
                  {para}
                </motion.p>
              ))}
            </div>
          </article>

          {linkedProduct && (
            <section className="bg-cream pb-16">
              <div className="max-w-3xl mx-auto px-6">
                <div
                  data-testid="article-product-link"
                  className="rounded-3xl border border-gold/40 bg-gold/5 p-6 md:p-8 flex flex-wrap items-center gap-6"
                >
                  <img
                    src={linkedProduct.image}
                    alt={`PHUDG ${linkedProduct.name}`}
                    className="w-20 h-24 object-cover rounded-2xl"
                  />
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-amberbrand mb-1">Made with</p>
                    <p className="font-serif text-2xl text-ink">{linkedProduct.name}</p>
                    {linkedProduct.price && (
                      <p className="text-sm font-light text-ink/50 mt-1">
                        ₹{linkedProduct.price} / {linkedProduct.unit} · Free delivery
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/oils/${linkedProduct.slug}`}
                    data-testid="article-shop-link"
                    className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream hover:bg-gold hover:text-ink transition-colors duration-300"
                  >
                    Shop this oil <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </section>
          )}

          {others.length > 0 && (
            <section className="bg-cream pb-24">
              <div className="max-w-3xl mx-auto px-6">
                <p className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-8">Keep reading</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  {others.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/journal/${p.slug}`}
                      data-testid={`journal-related-${p.slug}`}
                      className="group rounded-3xl border border-ink/10 bg-white/70 p-6 hover:border-gold/60 transition-colors duration-300"
                    >
                      <span className="text-[10px] tracking-[0.3em] uppercase text-amberbrand">{p.category}</span>
                      <h3 className="font-serif text-xl text-ink mt-2 group-hover:text-amberbrand transition-colors duration-300 flex items-start gap-2">
                        {p.title}
                        <ArrowRight size={16} className="mt-1 shrink-0 text-ink/25 group-hover:text-gold transition-colors duration-300" />
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}
      <Footer />
    </main>
  );
}
